import { useEffect, useState } from 'react';
import { formatEventTime } from '@utils/functions';
import { IState, StateContext } from '../../../../../state/stateContext';
import { useContext } from 'react';

interface UseOverviewDataProcessingProps {
    overviewData: any;
    eventId: string | null;
}

export const useOverviewDataProcessing = ({ overviewData, eventId }: UseOverviewDataProcessingProps) => {
    const [matchEnded, setMatchEnded] = useState<boolean>(false)
    const [homeTeam, setHomeTeam] = useState<string>('')
    const [awayTeam, setAwayTeam] = useState<string>('')
    const [isHalfTimeFirstEvent, setIsHalfTimeFirstEvent] = useState<boolean>(false)
    const [isEventAfterHalfTime, setIsEventAfterHalfTime] = useState<boolean>(false)
    const [isFullTimeFirstEvent, setIsFullTimeFirstEvent] = useState<boolean>(false)
    const [isEventAfterFullTime, setIsEventAfterFullTime] = useState<boolean>(false)
    const [isExtraTimeFirstEvent, setIsExtraTimeFirstEvent] = useState<boolean>(false)
    const [isPenaltiesFirstEvent, setIsPenaltiesFirstEvent] = useState<boolean>(false)
    const [isEventAfterPenalties, setIsEventAfterPenalties] = useState<boolean>(false)
    const [matchHomeScore, setMatchHomeScore] = useState<number>(0)
    const [matchAwayScore, setMatchAwayScore] = useState<number>(0)
    const [overviewDataSorted, setOverviewDataSorted] = useState<any>(null);
    const [penaltyResult, setPenaltyResult] = useState<string | null>(null);
    const [penaltiesAreStarted, setPenaltiesAreStarted] = useState<boolean>(false)
    const [shouldShowPenaltyAfterMatchEnd, setShouldShowPenaltyAfterMatchEnd] = useState<boolean>(false);

    const ctx = useContext(StateContext);
    const { setStContext } = ctx || {};

    const sortAndModifyOverviewData = (data: any) => {
        const newData = { ...data };
        let halfTimeIndex = -1;
        let fullTimeIndex = -1;
        let extraTimeIndex = -1;

        newData.match_events = newData.match_events.map((event: any, index: number) => {
            if (event.event_type === 'HALF_TIME') halfTimeIndex = index;
            if (event.event_type === 'FULL_TIME') fullTimeIndex = index;
            if (event.event_type === 'EXTRA_TIME') extraTimeIndex = index;
            return { ...event, isTimePlusNeeded: false, index: index };
        });

        newData.match_events = newData.match_events.map((event: any, index: number) => {
            const time = parseInt(event.match_time);
            if (
                (time > 45 && index > halfTimeIndex) ||
                (time > 90 && halfTimeIndex !== -1 && index > fullTimeIndex) ||
                (time > 120 && fullTimeIndex !== -1 && index > extraTimeIndex)
            ) {
                event.isTimePlusNeeded = true;
            }
            return event;
        });

        return newData;
    };

    const calculateWidthOfEventIconAndTime = (formattedTime: string) => {
        const timeLength = formattedTime.length;
        if (timeLength <= 2) {
            return 16
        } else if (timeLength <= 3) {
            return 23
        } else if (timeLength <= 4) {
            return 30
        } else if (timeLength <= 5) {
            return 36
        } else if (timeLength <= 6) {
            return 43
        } else {
            return 16
        }
    };

    const findMaxWidthForEventIconAndTime = (events: any[]) => {
        const eventsWithTime = events.filter(event => event.match_time !== undefined);
        if (eventsWithTime.length === 0) return 16;
    
        const maxWidthEvent = eventsWithTime.reduce((max, event) => {
            const formattedTime = formatEventTime(event.match_time, event.isTimePlusNeeded);
            const width = calculateWidthOfEventIconAndTime(formattedTime);
            return width > max.width ? { event, width } : max;
        }, { event: null, width: 0 });
    
        return maxWidthEvent.width;
    };

    useEffect(() => {
        if (overviewData?.data) {
            const sortedData = sortAndModifyOverviewData(overviewData?.data);
            setOverviewDataSorted(sortedData);
            setHomeTeam(sortedData?.home_team)
            setAwayTeam(sortedData?.away_team)
            const maxWidth = findMaxWidthForEventIconAndTime(sortedData.match_events);

            setStContext((prev: IState) => ({
                ...prev,
                overviewEventAndTimeWidth: maxWidth
            }));

            const matchEvents = sortedData?.match_events
            if (matchEvents && matchEvents.length > 0) {
                setIsHalfTimeFirstEvent(matchEvents[0].event_type === 'HALF_TIME')
                setIsEventAfterHalfTime(matchEvents.some((event: any) => event.event_type === 'HALF_TIME') && matchEvents[0].event_type !== 'HALF_TIME')
                setIsFullTimeFirstEvent(matchEvents[0].event_type === 'FULL_TIME' || matchEvents[0].event_type === 'MATCH_ENDED')
                setIsEventAfterFullTime(matchEvents.some((event: any) => event.event_type === 'FULL_TIME') && matchEvents[0].event_type !== 'FULL_TIME')
                setIsExtraTimeFirstEvent(matchEvents[0].event_type === 'EXTRA_TIME')
                setIsPenaltiesFirstEvent(matchEvents[0].event_type === 'PENALTIES')
                setIsEventAfterPenalties(matchEvents.some((event: any) => event.event_type === 'PENALTIES') && matchEvents[0].event_type !== 'PENALTIES')
                setMatchEnded(matchEvents.some((event: any) => event.event_type === 'MATCH_ENDED'))
                setMatchHomeScore(sortedData?.match_clock?.home_score)
                setMatchAwayScore(sortedData?.match_clock?.away_score)
                const penaltiesEvent = matchEvents.find((event: any) => event.event_type === 'PENALTIES_GOAL' || event.event_type === 'PENALTIES_MISSED' || event.event_type === 'PENALTIES_SAVED');
                if (!penaltiesEvent) {
                    setPenaltyResult(null);
                }
                else {
                    setPenaltyResult(penaltiesEvent.home_score + '-' + penaltiesEvent.away_score);
                }

                if (matchEvents.length >= 2) {
                    const lastEvent = matchEvents[0];
                    const secondLastEvent = matchEvents[1];
                    
                    if (lastEvent.event_type === 'MATCH_ENDED' && secondLastEvent.period_id === 5) {
                        setShouldShowPenaltyAfterMatchEnd(true);
                    } else {
                        setShouldShowPenaltyAfterMatchEnd(false);
                    }
                } else {
                    setShouldShowPenaltyAfterMatchEnd(false);
                }
            }
        }
    }, [overviewData?.data, eventId, setStContext]);

    return {
        matchEnded,
        homeTeam,
        awayTeam,
        isHalfTimeFirstEvent,
        isEventAfterHalfTime,
        isFullTimeFirstEvent,
        isEventAfterFullTime,
        isExtraTimeFirstEvent,
        isPenaltiesFirstEvent,
        isEventAfterPenalties,
        matchHomeScore,
        matchAwayScore,
        overviewDataSorted,
        penaltyResult,
        penaltiesAreStarted,
        shouldShowPenaltyAfterMatchEnd
    };
}; 